import { useState, useMemo, useEffect, useRef } from "react"
import { Box, Checkbox, HStack, Input, Spinner, Stack, Tabs, Table } from "@chakra-ui/react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Button, Section, Text, toaster } from "@/components"
import { useAuth } from "@/contexts"
import { getDealManagers } from "@/api/deals"
import { PortalRole } from "@/types"
import {
  getPendingExtractions,
  getPortalExtractions,
  getPortalExtractionFilePreviewTokenUrl,
  confirmPortalExtraction,
  dismissPortalExtraction,
  bulkConfirmExtractions,
  bulkDismissExtractions,
  reconcileDealExtractions,
  type PortalExtractionRecord,
} from "@/api/portalExtraction"

// ─── Helpers ────────────────────────────────────────────────────────────────────

function formatDollars(v: number | null): string {
  if (v == null) return "—"
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function formatDate(d: string | null): string {
  if (!d) return "—"
  return d
}

function formatTimestamp(d: string | null): string {
  if (!d) return "—"
  return new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

function formatNumber(v: number | null | undefined): string {
  if (v == null) return "—"
  return v.toLocaleString()
}

function typeBadge(dataType: string): { label: string; bg: string; color: string } {
  if (dataType === "fmv") return { label: "FMV (409A)", bg: "green.800", color: "green.100" }
  return { label: "Strike Price", bg: "gray.700", color: "gray.200" }
}

function grantTypeBadge(grantType: string | null): { label: string; bg: string; color: string } | null {
  if (!grantType) return null
  if (grantType === "ISO") return { label: "ISO", bg: "blue.800", color: "blue.100" }
  if (grantType === "NSO") return { label: "NSO", bg: "purple.800", color: "purple.100" }
  return { label: "Shares", bg: "orange.800", color: "orange.100" }
}

function confidenceColor(c: number): string {
  if (c >= 0.9) return "green.400"
  if (c >= 0.7) return "yellow.400"
  return "red.400"
}

function statusBadge(status: string): { label: string; bg: string; color: string } {
  switch (status) {
    case "confirmed": return { label: "Confirmed", bg: "green.900", color: "green.200" }
    case "applied": return { label: "Applied (OG)", bg: "blue.900", color: "blue.200" }
    case "auto_resolved": return { label: "Auto-Resolved", bg: "teal.900", color: "teal.200" }
    case "dismissed": return { label: "Dismissed", bg: "gray.700", color: "gray.300" }
    default: return { label: status, bg: "gray.700", color: "gray.300" }
  }
}

// Group extractions by deal
function groupByDeal(records: PortalExtractionRecord[]): Map<number, { deal: PortalExtractionRecord["deal"]; records: PortalExtractionRecord[] }> {
  const map = new Map<number, { deal: PortalExtractionRecord["deal"]; records: PortalExtractionRecord[] }>()
  for (const r of records) {
    const existing = map.get(r.dealId)
    if (existing) {
      existing.records.push(r)
    } else {
      map.set(r.dealId, { deal: r.deal, records: [r] })
    }
  }
  return map
}

// ─── Component ──────────────────────────────────────────────────────────────────

// Emails that default to "All" instead of filtering to self
const ALL_BY_DEFAULT_EMAILS = new Set([
  "sean@esofund.com",
  "bill.yu@esofund.com",
  "august@esofund.com",
])

export const PortalExtractionComponent = () => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState("working")
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  // Manager filter state: null = "All", number = specific DM
  const [filterManagerId, setFilterManagerId] = useState<number | null>(null)
  const didApplyDefault = useRef(false)

  // Preview drawer state
  const [previewId, setPreviewId] = useState<number | null>(null)
  const [previewFileName, setPreviewFileName] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  // Confirm override state
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editValue, setEditValue] = useState("")
  const [editDate, setEditDate] = useState("")

  // ─── Manager list ──────────────────────────────────────────────────────────

  const managersQuery = useQuery<{ id: number; name: string; shortName: string }[]>({
    queryKey: ["dealManagers"],
    queryFn: getDealManagers,
    staleTime: 1000 * 60 * 10,
  })

  const managers = managersQuery.data ?? []

  // Auto-default: DMs filter to self, non-DMs see all
  useEffect(() => {
    if (didApplyDefault.current || !user || managers.length === 0) return
    didApplyDefault.current = true
    const isDm = user.userType === PortalRole.ESO_PERSON
    if (!isDm) return // non-DMs default to "All"
    if (ALL_BY_DEFAULT_EMAILS.has(user.email.toLowerCase())) return
    const self = managers.find((m) => m.id === user.id)
    if (self) setFilterManagerId(self.id)
  }, [user, managers])

  // ─── Queries ────────────────────────────────────────────────────────────────

  const pendingQuery = useQuery<PortalExtractionRecord[]>({
    queryKey: ["portalExtractions", "pending", filterManagerId],
    queryFn: () => getPendingExtractions(filterManagerId ?? undefined),
    staleTime: 1000 * 60 * 2,
  })

  const completedQuery = useQuery<PortalExtractionRecord[]>({
    queryKey: ["portalExtractions", "completed"],
    queryFn: async () => {
      const [confirmed, dismissed, applied, autoResolved] = await Promise.all([
        getPortalExtractions("confirmed"),
        getPortalExtractions("dismissed"),
        getPortalExtractions("applied"),
        getPortalExtractions("auto_resolved"),
      ])
      return [...confirmed, ...dismissed, ...applied, ...autoResolved].sort(
        (a, b) => new Date(b.confirmedAt ?? b.createdAt).getTime() - new Date(a.confirmedAt ?? a.createdAt).getTime()
      )
    },
    staleTime: 1000 * 60 * 2,
    enabled: activeTab === "completed",
  })

  // ─── Mutations ──────────────────────────────────────────────────────────────

  const confirmMutation = useMutation({
    mutationFn: ({ id, overrides }: { id: number; overrides?: { value?: number; date?: string } }) =>
      confirmPortalExtraction(id, overrides),
    onSuccess: (_data, vars) => {
      toaster.create({ title: `Extraction #${vars.id} confirmed`, type: "success" })
      invalidateAll()
      setEditingId(null)
    },
    onError: (err: any) => {
      toaster.create({ title: err?.response?.data?.error || "Confirm failed", type: "error" })
    },
  })

  const dismissMutation = useMutation({
    mutationFn: (id: number) => dismissPortalExtraction(id),
    onSuccess: (_data, id) => {
      toaster.create({ title: `Extraction #${id} dismissed`, type: "info" })
      invalidateAll()
    },
    onError: (err: any) => {
      toaster.create({ title: err?.response?.data?.error || "Dismiss failed", type: "error" })
    },
  })

  const bulkConfirmMutation = useMutation({
    mutationFn: (ids: number[]) => bulkConfirmExtractions(ids),
    onSuccess: (data) => {
      toaster.create({ title: `${data.confirmed} extraction(s) confirmed`, type: "success" })
      setSelectedIds(new Set())
      invalidateAll()
    },
    onError: (err: any) => {
      toaster.create({ title: err?.response?.data?.error || "Bulk confirm failed", type: "error" })
    },
  })

  const bulkDismissMutation = useMutation({
    mutationFn: (ids: number[]) => bulkDismissExtractions(ids),
    onSuccess: (data) => {
      toaster.create({ title: `${data.dismissed} extraction(s) dismissed`, type: "info" })
      setSelectedIds(new Set())
      invalidateAll()
    },
    onError: (err: any) => {
      toaster.create({ title: err?.response?.data?.error || "Bulk dismiss failed", type: "error" })
    },
  })

  const reconcileMutation = useMutation({
    mutationFn: (dealId: number) => reconcileDealExtractions(dealId),
    onSuccess: (data) => {
      if (data.resolved > 0) {
        toaster.create({ title: `${data.resolved} extraction(s) auto-resolved`, type: "success" })
      } else {
        toaster.create({ title: "No matches found to auto-resolve", type: "info" })
      }
      invalidateAll()
    },
    onError: (err: any) => {
      toaster.create({ title: err?.response?.data?.error || "Reconcile failed", type: "error" })
    },
  })

  function invalidateAll() {
    queryClient.invalidateQueries({ queryKey: ["portalExtractions"] })
  }

  // ─── Selection ────────────────────────────────────────────────────────────

  const toggleSelection = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    const pending = pendingQuery.data ?? []
    if (selectedIds.size === pending.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(pending.map((r) => r.id)))
    }
  }

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleStartEdit = (record: PortalExtractionRecord) => {
    setEditingId(record.id)
    setEditValue(record.value != null ? String(record.value) : "")
    setEditDate(record.date ?? "")
  }

  const handleConfirm = (record: PortalExtractionRecord) => {
    if (editingId === record.id) {
      const overrides: { value?: number; date?: string } = {}
      const parsedValue = parseFloat(editValue)
      if (!isNaN(parsedValue) && parsedValue !== record.value) overrides.value = parsedValue
      if (editDate && editDate !== record.date) overrides.date = editDate
      confirmMutation.mutate({ id: record.id, overrides: Object.keys(overrides).length > 0 ? overrides : undefined })
    } else {
      confirmMutation.mutate({ id: record.id })
    }
  }

  const handleOpenPreview = async (record: PortalExtractionRecord) => {
    setPreviewId(record.id)
    setPreviewFileName(record.fileName ?? "File Preview")
    setPreviewUrl(null)
    try {
      const url = await getPortalExtractionFilePreviewTokenUrl(record.id)
      setPreviewUrl(url)
    } catch {
      toaster.create({ title: "Failed to load file preview", type: "error" })
      setPreviewId(null)
    }
  }

  // ─── Grouped data ─────────────────────────────────────────────────────────

  const pending = pendingQuery.data ?? []
  const completed = completedQuery.data ?? []
  const dealGroups = useMemo(() => groupByDeal(pending), [pending])

  return (
    <HStack align="stretch" h="calc(100vh - 64px)" gap={0}>
      {/* Left side: table */}
      <Box flex={previewId ? "3" : "1"} overflow="auto" borderRight={previewId ? "1px solid" : "none"} borderColor="border.default">
        <Section>
          <Text fontSize="xl" fontWeight="bold" mb={4}>Portal Extractions</Text>

          {/* ── Manager filter bar ─────────────────────────────────────────── */}
          <HStack gap={2} mb={3} flexWrap="wrap">
            <Box
              as="button"
              px={3}
              py={1}
              borderRadius="full"
              fontSize="sm"
              fontWeight="500"
              cursor="pointer"
              bg={filterManagerId === null ? "blue.600" : "card.bg"}
              color={filterManagerId === null ? "white" : "text.muted"}
              border="1px solid"
              borderColor={filterManagerId === null ? "blue.600" : "border.default"}
              onClick={() => { setFilterManagerId(null); setSelectedIds(new Set()) }}
              _hover={{ opacity: 0.8 }}
            >
              All
            </Box>
            {managers.map((m) => (
              <Box
                key={m.id}
                as="button"
                px={3}
                py={1}
                borderRadius="full"
                fontSize="sm"
                fontWeight="500"
                cursor="pointer"
                bg={filterManagerId === m.id ? "blue.600" : "card.bg"}
                color={filterManagerId === m.id ? "white" : "text.muted"}
                border="1px solid"
                borderColor={filterManagerId === m.id ? "blue.600" : "border.default"}
                onClick={() => { setFilterManagerId(m.id); setSelectedIds(new Set()) }}
                _hover={{ opacity: 0.8 }}
              >
                {m.shortName || m.name}
              </Box>
            ))}
          </HStack>

          <Tabs.Root value={activeTab} onValueChange={(details: { value: string }) => setActiveTab(details.value)} variant="line">
            <Tabs.List borderColor="border.default">
              <Tabs.Trigger value="working">
                Pending{pending.length > 0 ? ` (${pending.length})` : ""}
              </Tabs.Trigger>
              <Tabs.Trigger value="completed">Completed</Tabs.Trigger>
            </Tabs.List>

            <Tabs.Content value="working">
              {/* Bulk action bar */}
              {selectedIds.size > 0 && (
                <HStack mt={3} mb={2} gap={2} px={2} py={2} bg="card.bg" borderRadius="md">
                  <Text fontSize="sm" fontWeight="500">{selectedIds.size} selected</Text>
                  <Button
                    size="xs"
                    colorPalette="green"
                    onClick={() => bulkConfirmMutation.mutate(Array.from(selectedIds))}
                    loading={bulkConfirmMutation.isPending}
                  >
                    Confirm All
                  </Button>
                  <Button
                    size="xs"
                    colorPalette="red"
                    variant="outline"
                    onClick={() => bulkDismissMutation.mutate(Array.from(selectedIds))}
                    loading={bulkDismissMutation.isPending}
                  >
                    Dismiss All
                  </Button>
                </HStack>
              )}

              <Stack mt={4} gap={4}>
                {pendingQuery.isLoading ? (
                  <Box display="flex" justifyContent="center" py={10}>
                    <Spinner size="lg" />
                  </Box>
                ) : pending.length === 0 ? (
                  <Box py={10} textAlign="center">
                    <Text color="text.muted">No pending extractions</Text>
                  </Box>
                ) : (
                  Array.from(dealGroups.entries()).map(([dealId, group]) => (
                    <Box key={dealId} border="1px solid" borderColor="border.default" borderRadius="lg" overflow="hidden">
                      {/* Deal header */}
                      <HStack px={4} py={2} bg="card.bg" justifyContent="space-between">
                        <HStack gap={3}>
                          <Text fontSize="sm" fontWeight="600">
                            {group.deal?.company?.name ?? "Unknown Company"}
                          </Text>
                          <Text fontSize="sm" color="text.muted">
                            {group.deal?.client ? `${group.deal.client.firstName} ${group.deal.client.lastName}` : ""}
                          </Text>
                          <Text fontSize="xs" color="text.muted">
                            {group.records.length} extraction(s)
                          </Text>
                        </HStack>
                        <HStack gap={2}>
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={() => reconcileMutation.mutate(dealId)}
                            loading={reconcileMutation.isPending}
                          >
                            Reconcile
                          </Button>
                          <Button
                            size="xs"
                            colorPalette="green"
                            onClick={() => bulkConfirmMutation.mutate(group.records.map((r) => r.id))}
                            loading={bulkConfirmMutation.isPending}
                          >
                            Confirm All
                          </Button>
                        </HStack>
                      </HStack>

                      <Table.Root size="sm">
                        <Table.Header>
                          <Table.Row bg="table.header">
                            <Table.ColumnHeader w="40px">
                              <Checkbox.Root
                                checked={group.records.every((r) => selectedIds.has(r.id))}
                                onCheckedChange={() => {
                                  const allSelected = group.records.every((r) => selectedIds.has(r.id))
                                  setSelectedIds((prev) => {
                                    const next = new Set(prev)
                                    for (const r of group.records) {
                                      if (allSelected) next.delete(r.id)
                                      else next.add(r.id)
                                    }
                                    return next
                                  })
                                }}
                              >
                                <Checkbox.HiddenInput />
                                <Checkbox.Control />
                              </Checkbox.Root>
                            </Table.ColumnHeader>
                            <Table.ColumnHeader color="text.primary">Type</Table.ColumnHeader>
                            <Table.ColumnHeader color="text.primary">Description</Table.ColumnHeader>
                            <Table.ColumnHeader color="text.primary">Value</Table.ColumnHeader>
                            <Table.ColumnHeader color="text.primary">Vested</Table.ColumnHeader>
                            <Table.ColumnHeader color="text.primary">Date</Table.ColumnHeader>
                            <Table.ColumnHeader color="text.primary">Confidence</Table.ColumnHeader>
                            <Table.ColumnHeader color="text.primary">Source</Table.ColumnHeader>
                            <Table.ColumnHeader color="text.primary">Actions</Table.ColumnHeader>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {group.records.map((record, idx) => {
                            const badge = typeBadge(record.dataType)
                            const gtBadge = grantTypeBadge(record.grantType)
                            const isEditing = editingId === record.id
                            return (
                              <Table.Row
                                key={record.id}
                                bg={idx % 2 === 0 ? "table.row" : "table.stripe"}
                                _hover={{ bg: "hover.bg" }}
                              >
                                <Table.Cell>
                                  <Checkbox.Root
                                    checked={selectedIds.has(record.id)}
                                    onCheckedChange={() => toggleSelection(record.id)}
                                  >
                                    <Checkbox.HiddenInput />
                                    <Checkbox.Control />
                                  </Checkbox.Root>
                                </Table.Cell>
                                <Table.Cell>
                                  <HStack gap={1}>
                                    <Box display="inline-block" bg={badge.bg} color={badge.color} px={2} py={0.5} borderRadius="md" fontSize="xs" fontWeight="600">
                                      {badge.label}
                                    </Box>
                                    {gtBadge && (
                                      <Box display="inline-block" bg={gtBadge.bg} color={gtBadge.color} px={2} py={0.5} borderRadius="md" fontSize="xs" fontWeight="600">
                                        {gtBadge.label}
                                      </Box>
                                    )}
                                  </HStack>
                                </Table.Cell>
                                <Table.Cell>
                                  <Text fontSize="sm">{record.grantDescription ?? "—"}</Text>
                                </Table.Cell>
                                <Table.Cell>
                                  {isEditing ? (
                                    <Input size="xs" type="number" step="0.01" value={editValue} onChange={(e) => setEditValue(e.target.value)} w="100px" bg="card.bg" />
                                  ) : (
                                    <Text fontSize="sm" fontWeight="500">{formatDollars(record.value)}</Text>
                                  )}
                                </Table.Cell>
                                <Table.Cell>
                                  <Text fontSize="sm">{formatNumber(record.vestedShares)}</Text>
                                </Table.Cell>
                                <Table.Cell>
                                  {isEditing ? (
                                    <Input size="xs" type="text" value={editDate} onChange={(e) => setEditDate(e.target.value)} w="120px" bg="card.bg" placeholder="YYYY-MM-DD" />
                                  ) : (
                                    <Text fontSize="sm">{formatDate(record.date)}</Text>
                                  )}
                                </Table.Cell>
                                <Table.Cell>
                                  <Text fontSize="sm" color={confidenceColor(record.confidence)}>
                                    {(record.confidence * 100).toFixed(0)}%
                                  </Text>
                                </Table.Cell>
                                <Table.Cell>
                                  {record.dropboxPath ? (
                                    <Text fontSize="sm" color="blue.400" cursor="pointer" _hover={{ textDecoration: "underline" }} onClick={() => handleOpenPreview(record)} maxW="160px" truncate>
                                      {record.fileName ?? "View File"}
                                    </Text>
                                  ) : (
                                    <Text fontSize="sm" color="text.muted">{record.fileName ?? "—"}</Text>
                                  )}
                                </Table.Cell>
                                <Table.Cell>
                                  <HStack gap={1}>
                                    {isEditing ? (
                                      <>
                                        <Button size="xs" colorPalette="green" onClick={() => handleConfirm(record)} loading={confirmMutation.isPending}>
                                          Save
                                        </Button>
                                        <Button size="xs" variant="outline" onClick={() => setEditingId(null)}>
                                          Cancel
                                        </Button>
                                      </>
                                    ) : (
                                      <>
                                        <Button size="xs" colorPalette="green" variant="outline" onClick={() => handleConfirm(record)}>
                                          Confirm
                                        </Button>
                                        {record.dataType === "fmv" && (
                                          <Button size="xs" variant="ghost" onClick={() => handleStartEdit(record)}>
                                            Edit
                                          </Button>
                                        )}
                                        <Button size="xs" colorPalette="red" variant="ghost" onClick={() => dismissMutation.mutate(record.id)} loading={dismissMutation.isPending}>
                                          Dismiss
                                        </Button>
                                      </>
                                    )}
                                  </HStack>
                                </Table.Cell>
                              </Table.Row>
                            )
                          })}
                        </Table.Body>
                      </Table.Root>
                    </Box>
                  ))
                )}
              </Stack>
            </Tabs.Content>

            <Tabs.Content value="completed">
              <Stack mt={4} gap={0}>
                {completedQuery.isLoading ? (
                  <Box display="flex" justifyContent="center" py={10}>
                    <Spinner size="lg" />
                  </Box>
                ) : completed.length === 0 ? (
                  <Box py={10} textAlign="center">
                    <Text color="text.muted">No completed extractions</Text>
                  </Box>
                ) : (
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row bg="table.header">
                        <Table.ColumnHeader color="text.primary">Company</Table.ColumnHeader>
                        <Table.ColumnHeader color="text.primary">Client</Table.ColumnHeader>
                        <Table.ColumnHeader color="text.primary">Type</Table.ColumnHeader>
                        <Table.ColumnHeader color="text.primary">Description</Table.ColumnHeader>
                        <Table.ColumnHeader color="text.primary">Value</Table.ColumnHeader>
                        <Table.ColumnHeader color="text.primary">Vested</Table.ColumnHeader>
                        <Table.ColumnHeader color="text.primary">Date</Table.ColumnHeader>
                        <Table.ColumnHeader color="text.primary">Status</Table.ColumnHeader>
                        <Table.ColumnHeader color="text.primary">By</Table.ColumnHeader>
                        <Table.ColumnHeader color="text.primary">When</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {completed.map((record, idx) => {
                        const badge = typeBadge(record.dataType)
                        const sBadge = statusBadge(record.status)
                        return (
                          <Table.Row key={record.id} bg={idx % 2 === 0 ? "table.row" : "table.stripe"}>
                            <Table.Cell><Text fontSize="sm" fontWeight="500">{record.companyName ?? "—"}</Text></Table.Cell>
                            <Table.Cell><Text fontSize="sm">{record.clientName ?? "—"}</Text></Table.Cell>
                            <Table.Cell>
                              <Box display="inline-block" bg={badge.bg} color={badge.color} px={2} py={0.5} borderRadius="md" fontSize="xs" fontWeight="600">{badge.label}</Box>
                            </Table.Cell>
                            <Table.Cell><Text fontSize="sm">{record.grantDescription ?? "—"}</Text></Table.Cell>
                            <Table.Cell><Text fontSize="sm">{formatDollars(record.value)}</Text></Table.Cell>
                            <Table.Cell><Text fontSize="sm">{formatNumber(record.vestedShares)}</Text></Table.Cell>
                            <Table.Cell><Text fontSize="sm">{formatDate(record.date)}</Text></Table.Cell>
                            <Table.Cell>
                              <Box display="inline-block" bg={sBadge.bg} color={sBadge.color} px={2} py={0.5} borderRadius="md" fontSize="xs" fontWeight="600">{sBadge.label}</Box>
                            </Table.Cell>
                            <Table.Cell><Text fontSize="sm">{record.confirmedBy?.name ?? "—"}</Text></Table.Cell>
                            <Table.Cell><Text fontSize="sm" color="text.muted">{formatTimestamp(record.confirmedAt ?? record.appliedAt)}</Text></Table.Cell>
                          </Table.Row>
                        )
                      })}
                    </Table.Body>
                  </Table.Root>
                )}
              </Stack>
            </Tabs.Content>
          </Tabs.Root>
        </Section>
      </Box>

      {/* Right side: file preview */}
      {previewId && (
        <Box flex="2" overflow="hidden" position="relative">
          <HStack px={4} py={2} borderBottom="1px solid" borderColor="border.default" bg="card.bg" justifyContent="space-between">
            <Text fontSize="sm" fontWeight="500" truncate maxW="300px">{previewFileName}</Text>
            <Button size="xs" variant="ghost" onClick={() => { setPreviewId(null); setPreviewUrl(null) }}>Close</Button>
          </HStack>
          {previewUrl ? (
            <iframe
              src={previewUrl}
              title="File Preview"
              style={{ width: "100%", height: "calc(100% - 40px)", border: "none" }}
            />
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" h="calc(100% - 40px)">
              <Spinner size="lg" />
            </Box>
          )}
        </Box>
      )}
    </HStack>
  )
}
